import { agCharts } from "@ag-grid-enterprise/charts";
import { Location } from "@angular/common";
import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgCartesianSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { Account } from "app/model/entities/entity-model";
import { DateFns } from "app/shared/date-fns";

import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { UtilFns } from "app/shared/util-fns";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface InvoiceDelayData {
  invoiceMonthStart: Date;
  invoiceCount: number;
  invoiceTotalAmt: number;
  invoiceDelayDays: number;
}

@Component({
  selector: "app-chart-invoice-delay",
  templateUrl: "./chart-invoice-delay.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartInvoiceDelayComponent
  extends DomainBaseComponent
  implements OnInit
{
  @Input() account: Account;
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];
  title: string;
  options: any;
  private chartData: InvoiceDelayData[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);

    // this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
    //   this.updateFromContext();
    // });
  }

  async ngOnInit() {
    // ngOnChanges happens right away, so nothing needed here
  }

  //this will called when data is passed from parent to child.
  async ngOnChanges(val) {
    this.isPageReady = false;
    await this.processChartData();
    this.isPageReady = true;
  }

  private async processChartData() {
    // this.title = this.getTitle();
    const data = await this.dbQueryService.getInvoiceDelayDataForChart(this.account?.id, this.includedAccountIds);
    DateFns.fixupDatesInProjection(data, ["invoiceMonthStart"]);
    this.chartData = data as InvoiceDelayData[];

    this.options = {
      title: {
        text: this.getTitle(),
      },
      data: data,
      series: [
        {
          type: "line",
          xKey: "invoiceMonthStart",
          yKey: "invoiceCount",
          yName: "Number of Invoices",
          tooltip: { renderer: this.renderer.bind(this) },
        },
        {
          type: "line",
          xKey: "invoiceMonthStart",
          yKey: "invoiceTotalAmt",
          yName: "Invoice Total $",
          tooltip: { renderer: this.renderer.bind(this) },
        },
        {
          type: "line",
          xKey: "invoiceMonthStart",
          yKey: "invoiceDelayDays",
          yName: "Days Delayed",
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
      axes: [
        {
          type: "time",
          nice: false,
          position: "bottom",
          tick: {
            count: agCharts.time.month,
          },
          label: {
            format: "%b %Y",
          },
          title: {
            text: "Month Invoiced",
          },
        },
        {
          type: "number",
          keys: ["invoiceCount"],
          position: "left",
          title: {
            text: "Number of invoices",
          },
        },
        {
          type: "number",
          keys: ["invoiceTotalAmt"],
          position: "right",
          title: {
            text: "Invoice Total $",
          },
          label: {
            formatter: (params) => {
              return this.UtilFns.fmtCurrencyShort(params.value);
            },
          },
        },
        {
          type: "number",
          keys: ["invoiceDelayDays"],
          position: "right",
          title: {
            text: "Days Delayed",
          },
        },
      ],
    };
  }

  getTitle() {
    const accountName =
      this.account == null ? "All accounts" : this.account.accountName;
    return `Invoice Delay Chart - ${accountName} `;
  }

  renderer(params: AgCartesianSeriesTooltipRendererParams) {
    // const value = +(params.yName);

    const datum = params.datum;
    let content: string;
    const yKey = params.yKey;
    const value = +datum[yKey];
    if (yKey == "invoiceTotalAmt") {
      content = "Invoice Total: " + UtilFns.fmtCurrencyShort(value);
    } else if (params.yKey == "invoiceCount") {
      content = "Invoice Count: " + value.toFixed(0);
    } else {
      content = "Avg Days Delayed: " + value.toFixed(0);
    }

    return {
      title: params.xName, // this.formatDateAsMonth(params.xValue),
      content: content,
    };
  }

  formatDateAsMonth(date: Date) {
    const options = { year: "numeric", month: "long" } as any;
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Invoice Delay");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:D1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:D2");

    worksheet.addRow([]);

    worksheet.addRow(["Month", "Invoice Count", "Total Amount", "Days Delayed"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(2).width = 15;
    worksheet.getColumn(3).width = 18;
    worksheet.getColumn(4).width = 15;

    this.chartData.forEach((item) => {
      worksheet.addRow([
        this.formatDateAsMonth(item.invoiceMonthStart),
        item.invoiceCount,
        item.invoiceTotalAmt,
        item.invoiceDelayDays,
      ]);
    });

    // Format currency column
    for (let row = 5; row <= worksheet.rowCount; row++) {
      worksheet.getCell(`C${row}`).numFmt = "$#,##0.00";
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Invoice_Delay_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): InvoiceDelayData[] {
    return this.chartData;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "invoiceMonthStart", headerName: "Month", flex: 2, valueFormatter: (p) => this.formatDateAsMonth(p.value) },
      { field: "invoiceCount", headerName: "Invoice Count", flex: 1 },
      { field: "invoiceTotalAmt", headerName: "Total Amount", flex: 1, valueFormatter: (p) => UtilFns.fmtCurrency(p.value) },
      { field: "invoiceDelayDays", headerName: "Days Delayed", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "Invoice_Delay";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
